// nearMissSocketService.js
// Centralized Socket.IO handling for Near Miss form realtime sync

import { io } from "socket.io-client";

let socket = null;
let connectHandler = null; // function(connected:boolean) => void
let visibilityHandler = null; // prevents Heroku H15 idle connection errors on iPad

const BACKEND_URL =
  process.env.REACT_APP_SOCKET_URL ||
  process.env.REACT_APP_API_URL ||
  (process.env.NODE_ENV === "development"
    ? "http://localhost:5000"
    : window.location.origin);

export const initializeNearMissSocket = () => {
  if (socket) return socket;
  socket = io(BACKEND_URL, {
    withCredentials: true,
    transports: ["websocket", "polling"],
    path: "/socket.io",
    reconnectionAttempts: 10,
    reconnectionDelay: 5000,
    reconnectionDelayMax: 30000,
    randomizationFactor: 0.5,
  });

  socket.on("connect", () => {
    if (typeof connectHandler === "function") connectHandler(true);
  });
  socket.on("disconnect", () => {
    if (typeof connectHandler === "function") connectHandler(false);
  });

  visibilityHandler = () => {
    if (!socket) return;
    if (document.hidden) {
      socket.disconnect();
    } else {
      socket.connect();
    }
  };
  document.addEventListener("visibilitychange", visibilityHandler);

  return socket;
};

export const onNearMissConnectionChange = (fn) => {
  connectHandler = typeof fn === "function" ? fn : null;
  if (connectHandler && socket) {
    connectHandler(Boolean(socket.connected));
  }
};

export const isNearMissSocketConnected = () =>
  Boolean(socket && socket.connected);

export const joinNearMiss = (nearMissId) =>
  new Promise((resolve) => {
    if (!socket) initializeNearMissSocket();
    if (!socket)
      return resolve({ success: false, error: "socket_init_failed" });
    socket.emit(
      "join_near_miss",
      { near_miss_id: nearMissId },
      (ack) => {
        resolve(ack || { success: false });
      }
    );
  });

export const leaveNearMiss = (nearMissId) =>
  new Promise((resolve) => {
    if (!socket) return resolve({ success: true });
    socket.emit(
      "leave_near_miss",
      { near_miss_id: nearMissId },
      (ack) => {
        resolve(ack || { success: true });
      }
    );
  });

export const emitNearMissPatch = (nearMissId, patch) =>
  new Promise((resolve) => {
    if (!socket || !socket.connected) {
      // No connection - should queue for retry
      return resolve({ sent: false, shouldQueue: true, error: null, data: null });
    }

    const payload = { near_miss_id: nearMissId, ...patch };

    // Add timeout for no response (network issues)
    const timeout = setTimeout(() => {
      resolve({ sent: true, shouldQueue: true, error: "timeout", data: null });
    }, 10000); // 10 second timeout

    socket.emit("autosave_near_miss", payload, (ack) => {
      clearTimeout(timeout);
      if (!ack) {
        // No ack at all - network issue, should queue
        resolve({ sent: true, shouldQueue: true, error: "no_response", data: null });
      } else if (ack.success) {
        // Success - don't queue, include full response data
        resolve({ sent: true, shouldQueue: false, error: null, data: ack });
      } else {
        // Server responded with error (validation, etc.) - DON'T queue
        resolve({ sent: true, shouldQueue: false, error: ack.error, data: ack });
      }
    });
  });

export const disconnectNearMissSocket = () => {
  if (socket) {
    socket.off("connect");
    socket.off("disconnect");
    if (visibilityHandler) {
      document.removeEventListener("visibilitychange", visibilityHandler);
      visibilityHandler = null;
    }
    connectHandler = null;
    socket.disconnect();
    socket = null;
  }
};
